import React, { useState } from "react";
import {
  SaPageShell,
  SaPageHeader,
  SaCard,
  SaTabs,
  SA,
} from "../shared/SuperAdminUi";

function IntegrationPanel({ icon, iconColor, brand, title, description, bullets, configNote }) {
  return (
    <SaCard>
      <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", alignItems: "flex-start" }}>
        <div
          style={{
            width: "56px",
            height: "56px",
            borderRadius: "14px",
            background: `${iconColor ?? SA.primary}18`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "26px",
            color: iconColor ?? SA.primary,
            flexShrink: 0,
          }}
        >
          <i className={icon} />
        </div>
        <div style={{ flex: "1 1 260px" }}>
          <p style={{ margin: "0 0 4px", fontSize: "12px", fontWeight: "800", color: iconColor ?? SA.primary, letterSpacing: "0.04em", textTransform: "uppercase" }}>
            {brand}
          </p>
          <h3 style={{ margin: "0 0 10px", fontSize: "17px", fontWeight: "800", color: SA.ink }}>{title}</h3>
          <p style={{ margin: "0 0 14px", fontSize: "14px", color: SA.muted, lineHeight: 1.6 }}>{description}</p>
          <ul style={{ margin: "0 0 0", paddingLeft: "18px", color: SA.ink, fontSize: "13px", lineHeight: 1.8 }}>
            {bullets.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
          {configNote && (
            <div
              style={{
                marginTop: "16px",
                padding: "12px 16px",
                borderRadius: "10px",
                background: `${iconColor ?? SA.primary}10`,
                border: `1px solid ${iconColor ?? SA.primary}30`,
                fontSize: "13px",
                color: SA.ink,
                display: "flex",
                alignItems: "flex-start",
                gap: "10px",
              }}
            >
              <i className="fas fa-info-circle" style={{ color: iconColor ?? SA.primary, marginTop: "2px" }} />
              <span>{configNote}</span>
            </div>
          )}
        </div>
      </div>
    </SaCard>
  );
}

function IntegrationSettings() {
  const [activeTab, setActiveTab] = useState("facebook");

  const tabs = [
    { key: "facebook",    label: "Facebook Ads",    icon: "fab fa-facebook" },
    { key: "google",      label: "Google Ads",       icon: "fab fa-google" },
    { key: "linkedin",    label: "LinkedIn",         icon: "fab fa-linkedin" },
    { key: "instagram",   label: "Instagram",        icon: "fab fa-instagram" },
    { key: "99acres",     label: "99acres",          icon: "fas fa-city" },
    { key: "messaging",   label: "SMS & WhatsApp",   icon: "fas fa-comment-dots" },
  ];

  return (
    <SaPageShell>
      <SaPageHeader
        title="Integration settings"
        subtitle="Overview of lead source channels supported by Next Follow Up. Per-client credentials are configured under each client's Integration tab."
      />

      <SaTabs tabs={tabs} activeKey={activeTab} onChange={setActiveTab} />

      {activeTab === "facebook" && (
        <IntegrationPanel
          icon="fab fa-facebook"
          iconColor="#1877F2"
          brand="Meta"
          title="Facebook Lead Ads"
          description="Connect a Meta app with Lead Ads permissions to pull leads into the CRM. Each agency stores its own App ID, App Secret, Access Token, and Lead Gen form ID."
          bullets={[
            "Super Admin: open a client → Integration tab → Facebook Lead Ads section.",
            "Rotate access tokens periodically; use long-lived tokens via token exchange.",
            "Webhook URL must match your deployed API host.",
            "Ensure your Meta App has leads_retrieval and pages_read_engagement permissions.",
          ]}
          configNote="Per-client config: Clients → open agency → Integration tab → Facebook Lead Ads."
        />
      )}

      {activeTab === "google" && (
        <IntegrationPanel
          icon="fab fa-google"
          iconColor="#4285F4"
          brand="Google"
          title="Google Ads Lead Form Extensions"
          description="Pull leads from Google Ads Lead Form Extensions using the Google Ads API (OAuth 2.0 + Developer Token). Leads submitted through Search, Display, or Performance Max campaigns flow directly into the CRM."
          bullets={[
            "Requires a Google Ads Developer Token (approved for Basic or Standard access).",
            "Create OAuth 2.0 credentials in Google Cloud Console (Web Application type).",
            "Run the OAuth consent flow once to obtain a Refresh Token with the ads scope.",
            "For Manager (MCC) accounts, provide both the MCC Customer ID and sub-account Customer ID.",
            "Super Admin: Clients → open agency → Integration tab → Google Ads section.",
          ]}
          configNote="Per-client config: Clients → open agency → Integration tab → Google Ads."
        />
      )}

      {activeTab === "linkedin" && (
        <IntegrationPanel
          icon="fab fa-linkedin"
          iconColor="#0A66C2"
          brand="LinkedIn"
          title="LinkedIn Lead Gen Forms"
          description="Capture leads from LinkedIn Lead Gen Forms via the LinkedIn Marketing API. When a user submits a sponsored Lead Gen Form, a webhook triggers a push into the CRM using the credentials below."
          bullets={[
            "Create an app in the LinkedIn Developer Portal with the Marketing Developer Platform product.",
            "Request rw_lead_gen_forms (or r_liteprofile + rw_leads) OAuth 2.0 scopes.",
            "Generate an Access Token using the 3-legged OAuth flow.",
            "Provide your LinkedIn Ad Account ID (URN) for lead retrieval.",
            "Super Admin: Clients → open agency → Integration tab → LinkedIn section.",
          ]}
          configNote="Per-client config: Clients → open agency → Integration tab → LinkedIn."
        />
      )}

      {activeTab === "instagram" && (
        <IntegrationPanel
          icon="fab fa-instagram"
          iconColor="#C13584"
          brand="Meta / Instagram"
          title="Instagram Lead Ads (Graph API)"
          description="Pull leads from Instagram Lead Ad campaigns via the Meta Graph API. This requires a Facebook App connected to the agency's Instagram Business account. Separate from Facebook Lead Ads — each Instagram account has its own credentials."
          bullets={[
            "Link the Instagram Business account to a Facebook Page in Meta Business Suite.",
            "Your Meta App needs leads_retrieval, pages_read_engagement, and instagram_basic permissions.",
            "Generate a long-lived Page Access Token with the required scopes.",
            "Locate the Instagram Business Account ID under Settings → Professional Account.",
            "Super Admin: Clients → open agency → Integration tab → Instagram section.",
          ]}
          configNote="Per-client config: Clients → open agency → Integration tab → Instagram."
        />
      )}

      {activeTab === "99acres" && (
        <IntegrationPanel
          icon="fas fa-city"
          iconColor="#E84545"
          brand="99acres"
          title="99acres XML / portal leads"
          description="Receive enquiries from 99acres via XML push or pull API. Configure the account API key and listing ID to map incoming enquiries to the right project in the CRM."
          bullets={[
            "Obtain the API key from the 99acres partner portal under Integrations.",
            "Map incoming XML fields (name, phone, email, project) to the CRM lead schema.",
            "Set a listing ID to route enquiries to the correct property project.",
            "Monitor backend logs for rejected or malformed XML payloads.",
            "Super Admin: Clients → open agency → Integration tab → 99acres section.",
          ]}
          configNote="Per-client config: Clients → open agency → Integration tab → 99acres."
        />
      )}

      {activeTab === "messaging" && (
        <IntegrationPanel
          icon="fas fa-comment-dots"
          iconColor="#7B61FF"
          brand="Twilio"
          title="SMS & WhatsApp outbound messaging"
          description="Each agency can enable Twilio-backed SMS and WhatsApp from their client record. Messages are sent only when the integration is enabled and the relevant channel toggle is on."
          bullets={[
            "Super Admin: Clients → open agency → Integration tab → SMS & WhatsApp.",
            "Provide Twilio Account SID, Auth Token, SMS From (E.164), and WhatsApp From (whatsapp:+…).",
            "Backend uses IMessagingService with clientId when workflows trigger sends.",
            "Auth tokens are never returned after save — leave blank on update to keep the existing secret.",
          ]}
          configNote="Per-client config: Clients → open agency → Integration tab → SMS & WhatsApp."
        />
      )}
    </SaPageShell>
  );
}

export default IntegrationSettings;
